import { SharedModule } from '@/app/shared/shared.module';
import { RegisterUserCompleteCommand, UserProxy } from '@/app/proxy/proxies';
import { Component, EventEmitter, OnInit, Output, ViewEncapsulation, inject } from '@angular/core';
import { RegisterationSteps } from '@/app/auth/shared/auth-enums';
import { registrationProgress } from '@/app/auth/shared/registration-progress';
import { AuthService } from '@/app/auth/shared/services/auth.service';
import { PasswordService } from '@/app/auth/shared/services/password.service';
import { MixpanelTrackerService, MixpanelTimerKeys } from '@/app/core/analytics';

@Component({
  selector: 'auth-register-pwd',
  imports: [SharedModule],
  providers: [UserProxy],
  templateUrl: './create-pwd.component.html',
  styleUrl: './create-pwd.component.less',
  encapsulation: ViewEncapsulation.None,
})
export class CreatePwdComponent implements OnInit {
  private readonly _mixpanelTracker = inject(MixpanelTrackerService);

  @Output() steperChange = new EventEmitter<RegisterationSteps>();

  registerModal: RegisterUserCompleteCommand = new RegisterUserCompleteCommand();
  confirmPassword = '';
  passwordFocused = false;

  // Error tracking for Mixpanel
  private hadPasswordRequirementsError = false;
  private hadPasswordMismatchError = false;

  constructor(
    private _authService: AuthService,
    private _passwordService: PasswordService
  ) {}

  ngOnInit(): void {
    const registerModal = this._authService.getRegisterModal();
    if (registerModal !== undefined) {
      this.registerModal = registerModal;
    } else {
      this.steperChange.emit(RegisterationSteps.PHONE);
    }

    // Start timer for tracking time spent on password creation
    this._mixpanelTracker.startTimer(MixpanelTimerKeys.PASSWORD_CREATION);
  }

  get progressValues(): number[] {
    return registrationProgress(RegisterationSteps.PASSWORD, this._authService.getPhoneOtpRequired());
  }

  backButton() {
    this.steperChange.emit(
      this._authService.getPhoneOtpRequired() === true
        ? RegisterationSteps.VERIFY_PHONE_OTP
        : RegisterationSteps.PHONE
    );
  }

  get isPasswordValid(): boolean {
    return this._passwordService.checkPasswordIsValid(this.registerModal?.password || '');
  }

  get doPasswordsMatch(): boolean {
    return this.registerModal?.password === this.confirmPassword;
  }

  get passwordValidationErrors(): string[] {
    const password = this.registerModal?.password || '';
    if (!password || this.isPasswordValid) {
      return [];
    }
    return this._passwordService.getPasswordValidationErrors(password);
  }

  onPasswordBlur(): void {
    this.passwordFocused = false;
    if (this.registerModal?.password && !this.isPasswordValid) {
      this.hadPasswordRequirementsError = true;
    }
  }

  onConfirmPasswordBlur(): void {
    if (this.registerModal?.password && this.confirmPassword && !this.doPasswordsMatch) {
      this.hadPasswordMismatchError = true;
    }
  }

  continueCreatePwdStep() {
    if (this.registerModal) {
      this._authService.setRegisterModal(this.registerModal);

      // Track Password Created event
      this._mixpanelTracker.trackPasswordCreated({
        'entry point': 'Register',
        'error - password requirements': this.hadPasswordRequirementsError,
        'error - passwords do not match': this.hadPasswordMismatchError,
        'time spent': this._mixpanelTracker.getElapsedTime(MixpanelTimerKeys.PASSWORD_CREATION),
      });

      const phoneOtpRequired = this._authService.getPhoneOtpRequired();
      if (phoneOtpRequired === true) {
        this.steperChange.emit(RegisterationSteps.ABOUT);
      } else if (phoneOtpRequired === false) {
        this.steperChange.emit(RegisterationSteps.EMAIL);
      } else {
        this.steperChange.emit(RegisterationSteps.PHONE);
      }
    }
  }
}
